import '/assets/styles/pages/listing.scss';
import { useEffect, useMemo, useState } from 'react';

// Functions
import isExact from '@wearetla/tla-essentials-tools/functions/is-exact';

// Partials
import { Input } from '/views/partials/forms';
import Loader from '/views/partials/loader';
import Pagination from '/views/partials/pagination';
import Eventbox from '/views/partials/eventbox';
import EmptyMessage from '/views/partials/empty-message';

// Context
import { useNavigator } from '@wearetla/tla-essentials-tools/utilities/navigator';
import { useLocations } from '/utilities/locations';
import { ListingProvider, useListing } from '@wearetla/tla-essentials-tools/utilities/listing';
import { useHead } from '@wearetla/tla-essentials-tools/utilities/head';
import { useGlobalEvents } from '@wearetla/tla-essentials-tools/utilities/global-events';
import { useGlobalState } from '@wearetla/tla-essentials-tools/utilities/global-state';
import { useTranslation } from '/utilities/i18n';

// Data
import { getListingFilters } from '/data/listing-filters/search';
// import sortingOptions from '/data/sorting-options';

// Services
import eventServices from '/services/event';

const defaultQuery = {
	page: '1',
	order: 'asc',
	limit: '20',
	venue: '',
	location: '',
	term: '',
};

const getEvents = (query) => {
	return eventServices.getEvents(query);
};

const parseHead = (searchText, t) => {
	return {
		title: t('Arama Sonuçları: "{{searchText}}"', { searchText }),
		description: t('"{{searchText}}" kelimesine dair etkinlik sonuçları.', { searchText }),
	};
};

const SearchProvider = ({ match, ...props }) => {
	const { language } = useTranslation();
	const externalParams = useMemo(
		() => ({
			term: match?.params?.term,
		}),
		[match?.params.term]
	);
	const listingFilters = useMemo(() => getListingFilters(language), [language]);

	return (
		<ListingProvider
			externalParams={externalParams}
			availableFilters={listingFilters}
			queryString={match?.params?.query}
			defaultQuery={defaultQuery}
		>
			<Search {...props} />
		</ListingProvider>
	);
};

const Search = ({ pageProps }) => {
	const { t } = useTranslation();
	const { triggerEvent } = useGlobalEvents();
	const { setNotFound } = useGlobalState();
	const { locationOptions, venueOptions: rawVenueOptions } = useLocations();
	const { query, inputQuery, outputData, setFilter, paginate } = useListing();
	const { redirect } = useNavigator();
	const { setHead } = useHead();

	const [{ events, pagination, fetchedQuery }, setData] = useState(
		pageProps?.productData ?? { events: null, fetchedQuery: null, pagination: null }
	);

	// const selectedSortOption = useMemo(() => {
	// 	return sortingOptions.find(o => o.value === inputQuery.order)
	// }, [inputQuery])

	const selectedVenue = useMemo(
		() => rawVenueOptions?.find((f) => f.value === (query.venue || false)) ?? null,
		[rawVenueOptions, query]
	);

	const selectedLocation = useMemo(() => {
		return locationOptions.find((l) => {
			return (l.value || '') === (inputQuery.location || '');
		});
	}, [inputQuery, locationOptions]);

	const venueOptions = useMemo(() => (
		selectedLocation?.value ? rawVenueOptions.filter(venue => !venue?.city || venue.city.value === selectedLocation.value) : rawVenueOptions
	), [rawVenueOptions, selectedLocation])

	useEffect(() => {
		setHead(parseHead(inputQuery.term, t));
	}, [inputQuery, t]);

	useEffect(() => {
		if (inputQuery.term && !isExact(fetchedQuery, inputQuery)) {
			setData({ events: null, pagination: null, fetchedQuery: inputQuery });
			getEvents(inputQuery)
				.then((productData) => {
					if (inputQuery?.term) {
						triggerEvent('makeSearch', { term: inputQuery.term });
					}
					setData({ ...productData, fetchedQuery: inputQuery });
				})
				.catch(() => {
					setNotFound();
				});
		}
	}, [inputQuery, fetchedQuery]);

	useEffect(() => {
		if (inputQuery?.term === outputData?.externalParams?.term) {
			const params = {
				term: outputData.externalParams.term,
			};

			if (outputData.queryString && outputData.queryString.length) {
				params.query = outputData.queryString;
			}

			redirect('search', params);
		}
	}, [outputData, inputQuery]);

	return (
		<div className="listing-wrap wrapper">
			<h1 className="listing-title">
				{t('Arama Sonuçları: "{{searchText}}"', { searchText: inputQuery.term })}
			</h1>

			<nav className="listing-filters">
				<div className="filters-left">
					{/* <div className="filters-locations cities">
						<strong className="locations-title">Nerede?</strong>
						{locationOptions.map((location) => (
							<Link
								href={location?.slug ? 'eventsWithLocation' : 'events'}
								params={location?.slug ? { location: location.slug } : undefined}
								key={location?.id ?? 'empty'}
								className={classNames('locations-item', { selected: (location.city ?? '') === inputQuery?.location })}>
								<span className="item-check"></span>
								<span>{location.shortTitle}</span>
							</Link>
						))}
					</div> */}

					<div className="filters-selectinput filters-city">
						<Input
							label={t('Şehir')}
							z={2}
							type="select"
							onChange={(c) => {
								setFilter('location', c.value);
								setFilter('venue', '');
							}}
							value={selectedLocation}
							options={locationOptions}
						/>
					</div>
					<div className="filters-selectinput filters-venue">
						<Input
							label={t('Şube')}
							z={1}
							type="select"
							onChange={(c) => {
								setFilter('venue', c.value);
							}}
							value={selectedVenue}
							options={venueOptions}
						/>
					</div>
				</div>

				{/* <Input
					className="filters-sorting"
					type="select"
					value={selectedSortOption}
					onChange={(option) => {
						setFilter('order', option.value);
					}}
					options={sortingOptions} /> */}
			</nav>

			<div className="listing-results loader-container">
				<Loader loading={events === null} />
				{events && events.length > 0 && (
					<>
						<ul className="results-events">
							{events.map((event) => (
								<li className="listing-event" key={event.calendar_id}>
									<Eventbox event={event} />
								</li>
							))}
						</ul>
						<Pagination className="results-pagination" data={pagination} onPaginate={paginate} />
					</>
				)}
				{events?.length === 0 && <EmptyMessage>{t('Etkinlik Bulunamadı.')}</EmptyMessage>}
			</div>
		</div>
	);
};

export default SearchProvider;
